import React, { useState, useEffect } from 'react';
import { Users, Coins, FileText, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ExpenseCard, type Expense } from './ExpenseCard';
import { EmployeeCard } from './EmployeeCard';

import { EmployeeExpensesModal } from './EmployeeExpensesModal';
import { RejectExpenseModal } from './RejectExpenseModal';
import { Expenses, Profiles } from '@/integrations/api';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/use-toast';

interface Employee {
  id: string;
  name: string;
  totalOwedByCurrency: Record<string, number>;
  pendingByCurrency: Record<string, number>;
  recordedByCurrency: Record<string, number>;
  pendingExpenses: number;
}

export function AdminDashboard() {
  const { userRole } = useAuth();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEmployeeExpensesModal, setShowEmployeeExpensesModal] = useState(false);
  const [selectedEmployeeExpenses, setSelectedEmployeeExpenses] = useState<Expense[]>([]);
  const [selectedEmployeeName, setSelectedEmployeeName] = useState<string>('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [expenseToReject, setExpenseToReject] = useState<Expense | null>(null);

  const fetchExpenses = async () => {
    try {
      // First get all expenses (backend sorts newest-first)
      const expensesData = await Expenses.listAll();

      // Then get all profiles
      const profilesData = await Profiles.listAll();

      // Create a map of user_id to profile
      const profileMap = new Map(
        profilesData.map(profile => [profile.user_id, profile])
      );

      // Transform database data to match Expense interface
      const transformedExpenses: Expense[] = expensesData.map(expense => {
        const profile = profileMap.get(expense.user_id);
        return {
          id: expense.id,
          amount: Number(expense.amount),
          description: expense.description || 'No description',
          expenseType: expense.expense_type || undefined,
          paymentMethod: expense.payment_method || undefined,
          date: expense.receipt_date || expense.submitted_at.split('T')[0],
          submittedAt: expense.submitted_at,
          paidBy: expense.user_name || profile?.display_name || profile?.email || 'Unknown',
          status: expense.status as 'pending' | 'recorded' | 'paid' | 'rejected',
          receiptUrl: expense.receipt_url || undefined,
          currency: expense.currency || 'DKK',
          participants: expense.participants || undefined,
          rejectionReason: expense.rejection_reason || undefined
        };
      });

      setExpenses(transformedExpenses);
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  const fetchEmployees = async () => {
    try {
      // Get all expenses with user information
      const expensesData = await Expenses.listAll();

      // Get all profiles
      const profilesData = await Profiles.listAll();

      // Create profile map
      const profileMap = new Map(
        profilesData.map(profile => [profile.user_id, profile])
      );

      // Calculate totals for each employee
      const employeeMap = new Map();
      
      expensesData.forEach(expense => {
        const userId = expense.user_id;
        const profile = profileMap.get(userId);
        
        if (!employeeMap.has(userId)) {
          employeeMap.set(userId, {
            id: userId,
            name: profile?.display_name || profile?.email || 'Unknown',
            totalOwedByCurrency: {},
            pendingByCurrency: {},
            recordedByCurrency: {},
            pendingExpenses: 0
          });
        }
        
        const employee = employeeMap.get(userId);
        const currency = expense.currency || 'DKK';
        const amount = Number(expense.amount);
        
        if (expense.status !== 'paid' && expense.status !== 'rejected') {
          employee.totalOwedByCurrency[currency] = (employee.totalOwedByCurrency[currency] || 0) + amount;
        }
        if (expense.status === 'pending') {
          employee.pendingByCurrency[currency] = (employee.pendingByCurrency[currency] || 0) + amount;
          employee.pendingExpenses += 1;
        }
        if (expense.status === 'recorded') {
          employee.recordedByCurrency[currency] = (employee.recordedByCurrency[currency] || 0) + amount;
        }
      });

      setEmployees(Array.from(employeeMap.values()));
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchExpenses(), fetchEmployees()]);
      setLoading(false);
    };

    if (userRole === 'admin' || userRole === 'manager' || userRole === 'bookkeeper') {
      loadData();
    }
  }, [userRole]);


  const handleStatusChange = async (id: string, status: Expense['status']) => {
    try {
      // Backend sets reviewed_at/reviewed_by itself.
      await Expenses.changeStatus(id, status);

      // Update local state
      setExpenses(prev => 
        prev.map(expense => 
          expense.id === id ? { ...expense, status } : expense
        )
      );

      // Update modal expenses if modal is open
      setSelectedEmployeeExpenses(prev => 
        prev.map(expense => 
          expense.id === id ? { ...expense, status } : expense
        )
      );

      toast({
        title: "Success",
        description: `Expense marked as ${status}`,
      });

      // Refresh employee data as totals may have changed
      await fetchEmployees();
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  const handleEmployeeViewDetails = async (employeeId: string) => {
    try {
      // Get employee expenses (backend sorts newest-first)
      const expensesData = await Expenses.listAll(employeeId);

      // Transform to match Expense interface
      const profileData = await Profiles.get(employeeId).catch(() => null);

      const transformedExpenses: Expense[] = expensesData.map(expense => ({
        id: expense.id,
        amount: Number(expense.amount),
        description: expense.description || 'No description',
        expenseType: expense.expense_type || undefined,
        paymentMethod: expense.payment_method || undefined,
        date: expense.receipt_date || expense.submitted_at.split('T')[0],
        submittedAt: expense.submitted_at,
        paidBy: expense.user_name || profileData?.display_name || profileData?.email || 'Unknown',
        status: expense.status as 'pending' | 'recorded' | 'paid' | 'rejected',
        receiptUrl: expense.receipt_url || undefined,
        currency: expense.currency || 'DKK',
        participants: expense.participants || undefined,
        rejectionReason: expense.rejection_reason || undefined
      }));

      setSelectedEmployeeExpenses(transformedExpenses);
      setSelectedEmployeeName(profileData?.display_name || profileData?.email || 'Unknown');
      setShowEmployeeExpensesModal(true);
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  const handleEmployeeMarkPaid = async (employeeId: string) => {
    try {
      // Update all recorded expenses for this employee to paid
      await Expenses.markRecordedAsPaid(employeeId);

      // Refresh data
      await Promise.all([fetchExpenses(), fetchEmployees()]);

      toast({
        title: "Success",
        description: "All recorded expenses marked as paid",
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  const handleReject = (expense: Expense) => {
    setExpenseToReject(expense);
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async (reason: string) => {
    if (!expenseToReject) return;

    try {
      // Backend sets reviewed_at/reviewed_by itself.
      await Expenses.reject(expenseToReject.id, reason);

      // Update local state
      setExpenses(prev => 
        prev.map(expense => 
          expense.id === expenseToReject.id 
            ? { ...expense, status: 'rejected' as const, rejectionReason: reason } 
            : expense
        )
      );

      // Update modal expenses if modal is open
      setSelectedEmployeeExpenses(prev => 
        prev.map(expense => 
          expense.id === expenseToReject.id 
            ? { ...expense, status: 'rejected' as const, rejectionReason: reason } 
            : expense
        )
      );

      toast({
        title: "Success",
        description: "Expense has been rejected",
      });

      // Refresh employee data
      await fetchEmployees();
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };


  // Check if user has permission to access admin dashboard
  if (userRole !== 'admin' && userRole !== 'manager' && userRole !== 'bookkeeper') {
    return (
      <div className="min-h-screen bg-gradient-surface flex items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You don't have permission to access this area.</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-surface flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const pendingExpenses = expenses.filter(e => e.status === 'pending');
  const pendingByCurrency: Record<string, number> = {};
  pendingExpenses.forEach(e => {
    const c = e.currency || 'DKK';
    pendingByCurrency[c] = (pendingByCurrency[c] || 0) + e.amount;
  });
  const recordedByCurrency: Record<string, number> = {};
  expenses.filter(e => e.status === 'recorded').forEach(e => {
    const c = e.currency || 'DKK';
    recordedByCurrency[c] = (recordedByCurrency[c] || 0) + e.amount;
  });

  return (
    <div className="min-h-screen bg-gradient-surface p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
          <p className="text-muted-foreground">Manage employee expenses and reimbursements</p>
        </div>
        {userRole === 'admin' && (
          <Button onClick={() => navigate('/users')} className="gap-2">
            <Users className="h-4 w-4" />
            Manage Users
          </Button>
        )}
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-primary text-primary-foreground shadow-medium">
          <CardHeader className="pb-3">
             <CardTitle className="flex items-center gap-2 text-base">
              <Coins className="h-4 w-4" />
              Not Yet Recorded
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {Object.entries(pendingByCurrency).map(([currency, amount]) => (
                <div key={currency} className="text-2xl font-bold">{amount.toFixed(2)} {currency}</div>
              ))}
              {Object.keys(pendingByCurrency).length === 0 && <div className="text-2xl font-bold">0.00</div>}
            </div>
            <p className="text-primary-foreground/80 text-sm">
              {pendingExpenses.length} expenses
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-accent text-accent-foreground shadow-medium">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4" />
              Ready to Pay
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {Object.entries(recordedByCurrency).map(([currency, amount]) => (
                <div key={currency} className="text-2xl font-bold">{amount.toFixed(2)} {currency}</div>
              ))}
              {Object.keys(recordedByCurrency).length === 0 && <div className="text-2xl font-bold">0.00</div>}
            </div>
            <p className="text-accent-foreground/80 text-sm">
              {expenses.filter(e => e.status === 'recorded').length} recorded
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-soft">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" />
              Active Employees
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{employees.filter(e => e.pendingExpenses > 0).length}</div>
            <p className="text-muted-foreground text-sm">
              with pending expenses
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="expenses" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="expenses">Expense Management</TabsTrigger>
          <TabsTrigger value="firmakort">Firmakort</TabsTrigger>
          <TabsTrigger value="employees">Employee Overview</TabsTrigger>
        </TabsList>

        <TabsContent value="expenses" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Not Yet Recorded Expenses
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingExpenses.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No pending expenses</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {pendingExpenses.map((expense) => (
                    <ExpenseCard
                      key={expense.id}
                      expense={expense}
                      onStatusChange={handleStatusChange}
                      onReject={handleReject}
                      showActions={true}
                      isAdminView={true}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Recorded Expenses
              </CardTitle>
            </CardHeader>
            <CardContent>
              {expenses.filter(e => e.status === 'recorded').length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No recorded expenses</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {expenses.filter(e => e.status === 'recorded').map((expense) => (
                    <ExpenseCard
                      key={expense.id}
                      expense={expense}
                      onStatusChange={handleStatusChange}
                      onReject={handleReject}
                      showActions={true}
                      isAdminView={true}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="firmakort" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Company-card expenses
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Paid by company card — the employee is not reimbursed. Upload the receipts to Dinero.
              </p>
            </CardHeader>
            <CardContent>
              {expenses.filter(e => e.paymentMethod === 'firmakort').length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No company-card expenses</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {expenses.filter(e => e.paymentMethod === 'firmakort').map((expense) => (
                    <ExpenseCard
                      key={expense.id}
                      expense={expense}
                      showActions={false}
                      isAdminView={true}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employees" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Employee Reimbursements
              </CardTitle>
            </CardHeader>
            <CardContent>
              {employees.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No employees with pending expenses</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {employees.map((employee) => (
                    <EmployeeCard 
                      key={employee.id} 
                      employee={employee}
                      onViewDetails={handleEmployeeViewDetails}
                      onMarkPaid={handleEmployeeMarkPaid}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>


      {/* Employee Expenses Modal */}
      <EmployeeExpensesModal
        open={showEmployeeExpensesModal}
        onClose={() => {
          setShowEmployeeExpensesModal(false);
          setSelectedEmployeeExpenses([]);
          setSelectedEmployeeName('');
        }}
        expenses={selectedEmployeeExpenses}
        employeeName={selectedEmployeeName}
        onStatusChange={handleStatusChange}
        onReject={handleReject}
      />

      {/* Reject Expense Modal */}
      <RejectExpenseModal
        open={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setExpenseToReject(null);
        }}
        onReject={handleRejectConfirm}
        expenseName={expenseToReject?.description}
      />

    </div>
  );
}